import React, { useLayoutEffect, useRef, useState } from "react";
import { IconClose, IconSearch } from "../icons";

export default function AppInput({
  size = "md",
  invalid = false,
  className = "",
  style,
  type = "text",
  clearable = false,
  value,
  defaultValue = "",
  onChange,
  disabled = false,
  readOnly = false,
  trailingText = null,
  hideTrailingTextWhenEmpty = false,
  placeholder,
  ...props
}) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [trailingSlotWidth, setTrailingSlotWidth] = useState(0);
  const [valueWidth, setValueWidth] = useState(0);
  const currentValue = isControlled ? value : internalValue;
  const isSearch = type === "search";
  const showClear = clearable && isSearch && !disabled && !readOnly && String(currentValue ?? "").length > 0;
  const displayValue = String(currentValue ?? "");
  const measuredValue = displayValue || String(placeholder ?? "");
  const trailingTextContent = trailingText === null || trailingText === undefined ? "" : String(trailingText);
  const hasTrailingText = trailingTextContent.length > 0;
  const visibleTrailingText = hideTrailingTextWhenEmpty && !displayValue
    ? ""
    : trailingTextContent;
  const hasVisibleTrailingText = visibleTrailingText.length > 0;
  const shouldWrapInput = isSearch || hasTrailingText;
  const trailingSlotRef = useRef(null);
  const valueMeasureRef = useRef(null);

  const classes = [
    "ds-input",
    isSearch ? "ds-input--search" : "",
    isSearch ? "ds-input--with-leading-icon" : "",
    showClear ? "ds-input--with-clear" : "",
    hasVisibleTrailingText ? "ds-input--with-trailing-text" : "",
    size === "sm" ? "ds-control--sm" : "",
    size === "md" ? "ds-control--md" : "",
    invalid ? "ds-control--invalid" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  useLayoutEffect(() => {
    if (!hasTrailingText) {
      setTrailingSlotWidth(0);
      return undefined;
    }

    function updateTrailingSlotWidth() {
      if (!trailingSlotRef.current) {
        setTrailingSlotWidth(0);
      } else {
        setTrailingSlotWidth(Math.ceil(trailingSlotRef.current.getBoundingClientRect().width));
      }

      if (!valueMeasureRef.current) {
        setValueWidth(0);
        return;
      }

      setValueWidth(Math.ceil(valueMeasureRef.current.getBoundingClientRect().width));
    }

    updateTrailingSlotWidth();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateTrailingSlotWidth())
        : null;

    if (resizeObserver && trailingSlotRef.current) {
      resizeObserver.observe(trailingSlotRef.current);
    }

    return () => {
      resizeObserver?.disconnect();
    };
  }, [hasTrailingText, measuredValue, showClear, visibleTrailingText]);

  function handleChange(event) {
    if (!isControlled) {
      setInternalValue(event.target.value);
    }
    if (onChange) {
      onChange(event);
    }
  }

  function handleClear() {
    if (!isControlled) {
      setInternalValue("");
    }
    if (onChange) {
      onChange({ target: { value: "" }, currentTarget: { value: "" } });
    }
  }

  const wrapperStyle = hasTrailingText
    ? {
        "--ds-input-content-start-offset": isSearch
          ? "var(--ds-control-affix-content-offset-start)"
          : "var(--ds-control-inset-inline)",
        "--ds-input-trailing-slot-width": `${trailingSlotWidth}px`,
        "--ds-input-value-width": `${valueWidth}px`,
      }
    : undefined;

  const inputNode = (
    <input
      className={classes}
      type={type}
      style={style}
      value={currentValue ?? ""}
      disabled={disabled}
      readOnly={readOnly}
      placeholder={placeholder}
      aria-invalid={invalid ? "true" : undefined}
      onChange={handleChange}
      {...props}
    />
  );

  if (!shouldWrapInput) {
    return inputNode;
  }

  return (
    <div className="ds-input-wrap" style={wrapperStyle}>
      {isSearch ? (
        <span className="ds-input__leading-icon" aria-hidden="true">
          <IconSearch sx={{ fontSize: 16 }} />
        </span>
      ) : null}
      {hasTrailingText ? (
        <span className={`ds-input__value-measure ${size === "sm" ? "ds-control--sm" : "ds-control--md"}`} ref={valueMeasureRef} aria-hidden="true">
          {measuredValue}
        </span>
      ) : null}
      {inputNode}
      {hasTrailingText ? (
        <span className="ds-input__trailing-slot" ref={trailingSlotRef}>
          <span className="ds-input__trailing-text">{visibleTrailingText}</span>
          {showClear ? (
            <button type="button" className="ds-input__clear" aria-label="Eingabe löschen" onClick={handleClear}>
              <IconClose sx={{ fontSize: 16 }} />
            </button>
          ) : null}
        </span>
      ) : showClear ? (
        <button type="button" className="ds-input__clear" aria-label="Eingabe löschen" onClick={handleClear}>
          <IconClose sx={{ fontSize: 16 }} />
        </button>
      ) : null}
    </div>
  );
}
